import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { IntelligenceGroupBy, UnitIntelligenceService } from 'src/app/services/unit-intelligence.service';
import { UnitService } from 'src/app/services/unit.service';
import { DriverService } from 'src/app/services/driver.service';
import { ClientService } from 'src/app/services/client.service';
import { UtilsService } from 'src/app/services/utils.service';

const TITLES: Record<IntelligenceGroupBy, { title: string; entityPlural: string; column: string }> = {
    unit: { title: 'Ranking completo por unidad', entityPlural: 'unidades', column: 'Unidad' },
    driver: { title: 'Ranking completo por operador', entityPlural: 'operadores', column: 'Operador' },
    client: { title: 'Ranking completo por cliente', entityPlural: 'clientes', column: 'Cliente' },
};

@Component({
    selector: 'app-intelligence-ranking',
    templateUrl: './intelligence-ranking.component.html',
    styleUrls: ['./intelligence-ranking.component.css']
})
export class IntelligenceRankingComponent implements OnInit {

    readonly Math = Math;

    groupBy: IntelligenceGroupBy = 'unit';
    titles = TITLES.unit;

    filterFrom = '';
    filterTo = '';
    filterUnitId = '';
    filterDriverId = '';
    filterClientId = '';

    units: any[] = [];
    drivers: any[] = [];
    clients: any[] = [];

    data: any = null;
    loading = false;

    currentPage = 1;
    perPage = 10;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private svc: UnitIntelligenceService,
        private unitService: UnitService,
        private driverService: DriverService,
        private clientService: ClientService,
        private spinner: NgxSpinnerService,
        private utils: UtilsService
    ) { }

    ngOnInit(): void {
        this.route.params.subscribe(params => {
            const groupBy = params['groupBy'] as IntelligenceGroupBy;
            this.groupBy = TITLES[groupBy] ? groupBy : 'unit';
            this.titles = TITLES[this.groupBy];
            this.currentPage = 1;
            this.fetch();
        });

        const today = new Date();
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        this.filterFrom = firstOfMonth.toISOString().slice(0, 10);
        this.filterTo = today.toISOString().slice(0, 10);

        this.unitService.getInfo().subscribe({ next: (resp: any) => this.units = Array.isArray(resp) ? resp : (resp?.data || []) });
        this.driverService.getInfo().subscribe({ next: (resp: any) => this.drivers = Array.isArray(resp) ? resp : (resp?.data || []) });
        this.clientService.getInfo().subscribe({ next: (resp: any) => this.clients = Array.isArray(resp) ? resp : (resp?.data || []) });
    }

    fetch(): void {
        this.loading = true;
        this.spinner.show();
        const params: any = {
            from: this.filterFrom,
            to: this.filterTo,
            unit_id: this.filterUnitId,
            driver_id: this.filterDriverId,
            client_id: this.filterClientId,
            page: this.currentPage,
            per_page: this.perPage,
        };

        this.svc.getRanking(this.groupBy, params).subscribe({
            next: (resp: any) => {
                this.data = resp;
                this.loading = false;
                this.spinner.hide();
            },
            error: () => {
                this.loading = false;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo obtener el ranking', icon: 'error' });
            }
        });
    }

    applyFilters(): void {
        this.currentPage = 1;
        this.fetch();
    }

    clearFilters(): void {
        this.filterUnitId = '';
        this.filterDriverId = '';
        this.filterClientId = '';
        this.applyFilters();
    }

    goToPage(page: number): void {
        if (page < 1 || (this.data?.table?.pagination?.last_page && page > this.data.table.pagination.last_page)) return;
        this.currentPage = page;
        this.fetch();
    }

    get pageNumbers(): number[] {
        const lastPage = this.data?.table?.pagination?.last_page || 1;
        const windowSize = 3;
        const end = Math.min(lastPage, Math.max(1, this.currentPage - 1) + windowSize - 1);
        const start = Math.max(1, end - windowSize + 1);
        const pages: number[] = [];
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }

    goToHistory(id: number): void {
        this.router.navigate(['/unit/intelligence-center/history', this.groupBy, id]);
    }

    goBack(): void {
        this.router.navigateByUrl('/unit/intelligence-center');
    }

    formatCurrency(value: number | null | undefined): string {
        if (value === null || value === undefined) return '—';
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
    }

    formatKm(value: number | null | undefined): string {
        if (value === null || value === undefined) return '—';
        return `${new Intl.NumberFormat('es-MX').format(value)} km`;
    }

    costPerKmClass(value: number | null): string {
        if (value === null || value === undefined) return '';
        if (value <= 5) return 'cell-cost--low';
        if (value <= 8) return 'cell-cost--mid';
        return 'cell-cost--high';
    }

    barWidth(value: number, max: number): string {
        if (!max) return '0%';
        return `${Math.max(4, Math.min(100, (value / max) * 100))}%`;
    }
}
